import { useState, useEffect, useRef } from "react";
import { isConfigured } from "./lib/supabase";
import * as api from "./lib/api";
import { cad, resolveImg, SHOP_KINDS, kindLabel, fromPrice, hasSizes, onePriceVariant, ONE_PRICE_ID } from "./data";
import { Ic, P } from "./ui";

/* ------------------------- studio (admin) ------------------------- */
/* Shrink the photo in the browser before it goes anywhere: a 12 MP phone
   photo becomes a ~200 KB web image, which keeps the free storage tier
   comfortable and the gallery fast. Returns a JPEG blob. */
function shrinkImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("could not encode image"))),
          "image/jpeg",
          0.82
        );
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

const BLANK_ART = { title: "", price: 0, year: String(new Date().getFullYear()), medium: "Oil on canvas", dims: "", category: "", status: "available", featured: false, description: "", images: [] };

function ArtEditor({ initial, onSave, onCancel, notify }) {
  const [a, setA] = useState(initial);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const addImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const blob = await shrinkImageFile(file);
      if (isConfigured) {
        const { url, error } = await api.uploadPaintingImage(blob);
        if (error) { notify("Upload failed — " + (error.message || "please try again")); }
        else { setA((cur) => ({ ...cur, images: [...cur.images, url] })); }
      } else {
        const data = await blobToDataUrl(blob);
        setA((cur) => ({ ...cur, images: [...cur.images, data] }));
      }
    } catch {
      notify("That image couldn't be read — try a JPG or PNG");
    }
    setUploading(false);
  };
  return (
    <div style={{ background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: 12, padding: 22, marginTop: 16 }}>
      <div className="kg-caps" style={{ marginBottom: 14 }}>{initial.id ? "Edit painting" : "New painting"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }} className="kg-ed-grid">
        <div className="kg-field"><label htmlFor="e-title">Title</label><input id="e-title" className="kg-input" value={a.title} onChange={set("title")} /></div>
        <div className="kg-field"><label htmlFor="e-price">Price (CAD)</label><input id="e-price" className="kg-input" type="number" min="0" value={a.price} onChange={set("price")} /></div>
        <div className="kg-field"><label htmlFor="e-medium">Medium</label><input id="e-medium" className="kg-input" value={a.medium} onChange={set("medium")} /></div>
        <div className="kg-field"><label htmlFor="e-dims">Dimensions</label><input id="e-dims" className="kg-input" placeholder="16 × 20 in" value={a.dims} onChange={set("dims")} /></div>
        <div className="kg-field"><label htmlFor="e-year">Year</label><input id="e-year" className="kg-input" value={a.year} onChange={set("year")} /></div>
        <div className="kg-field"><label htmlFor="e-cat">Collection</label><input id="e-cat" className="kg-input" placeholder="Portraits, Seascapes…" value={a.category} onChange={set("category")} /></div>
      </div>
      <div className="kg-field"><label htmlFor="e-desc">Description</label><textarea id="e-desc" className="kg-textarea" rows={3} value={a.description} onChange={set("description")} /></div>
      <div className="kg-field"><label>Images (first is the cover)</label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {a.images.map((im, i) => (
            <div key={i} style={{ position: "relative" }}>
              <img src={resolveImg(im)} alt={"image " + (i + 1)} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />
              <button className="kg-iconbtn" aria-label={"Remove image " + (i + 1)} onClick={() => setA({ ...a, images: a.images.filter((_, j) => j !== i) })}
                style={{ position: "absolute", top: -8, right: -8, background: "var(--panel)", border: "1px solid var(--line)", padding: 3 }}><Ic d={P.x} size={12} /></button>
            </div>
          ))}
          <button className="kg-btn kg-btn-ghost" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
            <Ic d={P.plus} size={15} /> {uploading ? "Uploading…" : "Upload image"}</button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={addImage} aria-label="Upload painting image" />
        </div>
      </div>
      <div style={{ display: "flex", gap: 18, alignItems: "center", margin: "8px 0 18px", flexWrap: "wrap" }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <input type="checkbox" checked={a.status === "sold"} onChange={(e) => setA({ ...a, status: e.target.checked ? "sold" : "available" })} /> Mark as sold
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <input type="checkbox" checked={a.featured} onChange={set("featured")} /> Feature on home page
        </label>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="kg-btn kg-btn-primary" disabled={!a.title.trim() || a.images.length === 0}
          onClick={() => onSave({ ...a, price: Number(a.price) || 0 })}>Save painting</button>
        <button className="kg-btn kg-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
      {a.images.length === 0 && <p style={{ fontSize: 12, color: "var(--ink2)", marginBottom: 0 }}>Add at least one image to save.</p>}
      <style>{`@media (max-width:700px){ .kg-ed-grid{ grid-template-columns:1fr !important; } }`}</style>
    </div>
  );
}

/* Sizes are written in inches: Karima sells mainly into Canada and the US, and
   the label is free text, so anything typed here is exactly what a buyer reads. */
const BLANK_SHOP = { kind: "print", title: "", description: "", images: [], active: true,
  variants: [{ id: "", label: "", price: 0 }] };

/* A URL-safe id for a size, derived from its label so Karima never has to think
   about ids. "8 × 10 in" becomes "8-10-in". The id is what the cart
   stores, so it must not change once an item is out in the world — which is why
   an existing size keeps the id it was saved with. */
const slugify = (text) => String(text).toLowerCase()
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "size";

function ShopEditor({ initial, onSave, onCancel, notify }) {
  const [a, setA] = useState(initial);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const addImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const blob = await shrinkImageFile(file);
      if (isConfigured) {
        const { url, error } = await api.uploadPaintingImage(blob);
        if (error) notify("Upload failed — " + (error.message || "please try again"));
        else setA((cur) => ({ ...cur, images: [...cur.images, url] }));
      } else {
        const data = await blobToDataUrl(blob);
        setA((cur) => ({ ...cur, images: [...cur.images, data] }));
      }
    } catch {
      notify("That image couldn't be read — try a JPG or PNG");
    }
    setUploading(false);
  };

  const variants = a.variants || [];
  const sized = hasSizes(a.kind);
  const setVariant = (i, patch) => setA({
    ...a,
    variants: variants.map((v, j) => (j === i ? { ...v, ...patch } : v)),
  });
  const addVariant = () => setA({ ...a, variants: [...variants, { id: "", label: "", price: 0 }] });
  const removeVariant = (i) => setA({ ...a, variants: variants.filter((_, j) => j !== i) });

  /* Switching between a print and a sticker switches between many sizes and one
     price. Going to one price keeps the cheapest figure already typed, so the
     work isn't lost; going back to sizes leaves that figure as the first row. */
  const changeKind = (e) => {
    const kind = e.target.value;
    const nowSized = hasSizes(kind);
    if (nowSized === sized) { setA({ ...a, kind }); return; }
    if (!nowSized) {
      const cheapest = variants.map((v) => Number(v.price)).filter((n) => n > 0).sort((x, y) => x - y)[0] || 0;
      setA({ ...a, kind, variants: [onePriceVariant(cheapest)] });
    } else {
      const price = Number((variants[0] || {}).price) || 0;
      setA({ ...a, kind, variants: [{ id: "", label: "", price }] });
    }
  };

  const setOnePrice = (e) => setA({ ...a, variants: [onePriceVariant(e.target.value)] });
  const onePrice = (variants[0] || {}).price ?? "";

  /* Give any size still missing an id one based on its label, and refuse to save
     two sizes that would collide. */
  const prepared = variants.map((v, i) => ({
    ...v,
    price: Number(v.price) || 0,
    label: sized ? String(v.label || "") : "Standard",
    id: sized ? (String(v.id || "").trim() || slugify(v.label || `size-${i + 1}`)) : ONE_PRICE_ID,
  }));
  const ids = prepared.map((v) => v.id);
  const duplicateId = sized ? ids.find((id, i) => ids.indexOf(id) !== i) : null;
  /* A size needs a name; a single price does not. */
  const usable = prepared.filter((v) => v.price > 0 && (!sized || v.label.trim()));
  const canSave = a.title.trim() && a.images.length > 0 && usable.length > 0 && !duplicateId;

  return (
    <div style={{ background: "var(--panel2)", border: "1px solid var(--line)", borderRadius: 12, padding: 22, marginTop: 16 }}>
      <div className="kg-caps" style={{ marginBottom: 14 }}>{initial.id ? "Edit item" : "New item"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }} className="kg-ed-grid">
        <div className="kg-field"><label htmlFor="s-title">Title</label>
          <input id="s-title" className="kg-input" value={a.title} onChange={set("title")}
            placeholder={`Golden Horizon — ${kindLabel(a.kind).toLowerCase().replace(/s$/, "")}`} /></div>
        <div className="kg-field"><label htmlFor="s-kind">Shows under</label>
          <select id="s-kind" className="kg-select" value={a.kind} onChange={changeKind}>
            {SHOP_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
          </select></div>
      </div>
      <div className="kg-field"><label htmlFor="s-desc">Description</label>
        <textarea id="s-desc" className="kg-textarea" rows={3} value={a.description || ""} onChange={set("description")} /></div>

      <div className="kg-field"><label>Images (first is the cover)</label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {a.images.map((im, i) => (
            <div key={i} style={{ position: "relative" }}>
              <img src={resolveImg(im)} alt={"image " + (i + 1)} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />
              <button className="kg-iconbtn" aria-label={"Remove image " + (i + 1)} onClick={() => setA({ ...a, images: a.images.filter((_, j) => j !== i) })}
                style={{ position: "absolute", top: -8, right: -8, background: "var(--panel)", border: "1px solid var(--line)", padding: 3 }}><Ic d={P.x} size={12} /></button>
            </div>
          ))}
          <button className="kg-btn kg-btn-ghost" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
            <Ic d={P.plus} size={15} /> {uploading ? "Uploading…" : "Upload image"}</button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={addImage} aria-label="Upload item image" />
        </div>
      </div>

      {sized ? (
        <div className="kg-field">
          <label>Sizes and prices</label>
          <p className="kg-small kg-muted" style={{ margin: "0 0 10px" }}>
            One row per size a buyer can choose. The cheapest is what the shop page
            shows as “from”. Leave a single row if there is only one size.
          </p>
          {variants.map((v, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <input className="kg-input" style={{ flex: "2 1 220px" }} placeholder="8 × 10 in"
                aria-label={`Size ${i + 1} name`} value={v.label} onChange={(e) => setVariant(i, { label: e.target.value })} />
              <input className="kg-input" style={{ flex: "0 1 120px" }} type="number" min="0" placeholder="35"
                aria-label={`Size ${i + 1} price in dollars`} value={v.price} onChange={(e) => setVariant(i, { price: e.target.value })} />
              <button className="kg-iconbtn" aria-label={`Remove size ${i + 1}`} disabled={variants.length === 1}
                onClick={() => removeVariant(i)}><Ic d={P.trash} size={15} /></button>
            </div>
          ))}
          <button className="kg-btn kg-btn-ghost" onClick={addVariant}><Ic d={P.plus} size={15} /> Add a size</button>
        </div>
      ) : (
        <div className="kg-field" style={{ maxWidth: 220 }}>
          <label htmlFor="s-price">Price (CAD)</label>
          <input id="s-price" className="kg-input" type="number" min="0" placeholder="4"
            value={onePrice} onChange={setOnePrice} />
          <p className="kg-small kg-muted" style={{ margin: "6px 0 0" }}>
            {kindLabel(a.kind)} come in one size, so there is one price.
          </p>
        </div>
      )}

      <div style={{ display: "flex", gap: 18, alignItems: "center", margin: "8px 0 18px", flexWrap: "wrap" }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <input type="checkbox" checked={a.active !== false} onChange={(e) => setA({ ...a, active: e.target.checked })} /> Show in the shop
        </label>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button className="kg-btn kg-btn-primary" disabled={!canSave}
          onClick={() => onSave({ ...a, variants: usable })}>Save item</button>
        <button className="kg-btn kg-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
      {!canSave && (
        <p style={{ fontSize: 12, color: "var(--ink2)", margin: "10px 0 0" }}>
          {duplicateId
            ? "Two sizes have the same name — give them different ones."
            : sized
              ? "Needs a title, at least one image, and one size with a price above zero."
              : "Needs a title, at least one image, and a price above zero."}
        </p>
      )}
      <style>{`@media (max-width:700px){ .kg-ed-grid{ grid-template-columns:1fr !important; } }`}</style>
    </div>
  );
}

function Studio({ paintings, savePainting, patchPainting, reorderPaintings, removePainting, orders, patchOrder,
  shopItems, saveShopItem, patchShopItem, removeShopItem,
  messages, testimonials, addTestimonial, removeTestimonial, news, settings, saveSettings,
  notify, onSignOut, studioLoading }) {
  const [tab, setTab] = useState("art");
  const [editing, setEditing] = useState(null); // painting object or null
  const [editingShop, setEditingShop] = useState(null); // shop item or null
  const [shopKind, setShopKind] = useState("print"); // which tab of the shop list to show
  const [tName, setTName] = useState(""); const [tText, setTText] = useState("");
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const heroFileRef = useRef(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const aboutFileRef = useRef(null);
  const [aboutBusy, setAboutBusy] = useState(false);
  /* Home-page main image: any photo, not just paintings in the gallery. */
  const pickImage = (field) => async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const setBusy = field === "about_image" ? setAboutBusy : setHeroBusy;
    setBusy(true);
    try {
      const blob = await shrinkImageFile(file);
      if (isConfigured) {
        const { url, error } = await api.uploadPaintingImage(blob);
        if (error) notify("Upload failed — " + (error.message || "please try again"));
        else { setS((cur) => ({ ...cur, [field]: url })); notify("Image ready — press Save settings to publish it"); }
      } else {
        const data = await blobToDataUrl(blob);
        setS((cur) => ({ ...cur, [field]: data }));
        notify("Image ready — press Save settings to publish it");
      }
    } catch {
      notify("That image couldn't be read — try a JPG or PNG");
    }
    setBusy(false);
  };
  const pickHero = pickImage("hero_image");
  const pickAbout = pickImage("about_image");
  const [dragId, setDragId] = useState(null);
  const moveTo = (from, to) => {
    if (from < 0 || to < 0 || to >= paintings.length || from === to) return;
    const list = [...paintings];
    const [m] = list.splice(from, 1);
    list.splice(to, 0, m);
    reorderPaintings(list);
  };
  const tabs = [["art", "Artworks"], ["shop", "Prints & small things"], ["orders", "Orders & inquiries"], ["msg", "Messages"], ["quotes", "Testimonials"], ["news", "Newsletter"], ["set", "Settings"]];
  const handleSave = async (art) => {
    const ok = await savePainting(art);
    if (ok) setEditing(null);
  };
  const handleSaveShop = async (item) => {
    const ok = await saveShopItem(item);
    if (ok) setEditingShop(null);
  };
  return (
    <div className="kg-page kg-wrap kg-section">
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
        <div>
          <div className="kg-caps">Artist studio</div>
          <h2 className="kg-display" style={{ marginBottom: 6 }}>Manage your gallery</h2>
        </div>
        {isConfigured && (
          <button className="kg-btn kg-btn-ghost" style={{ marginLeft: "auto", padding: "8px 16px" }} onClick={onSignOut}>
            Sign out
          </button>
        )}
      </div>
      <p style={{ fontSize: 13, color: "var(--ink2)", maxWidth: "70ch" }}>
        {isConfigured
          ? "Changes here save straight to the gallery and everyone sees them right away. You are the only account that can edit — visitors can look, and nothing more."
          : "Local preview: this browser is the only place your changes are stored, and the studio isn't locked. Connect Supabase (see supabase/schema.sql) before the site goes live."}
      </p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "14px 0 8px" }}>
        {tabs.map(([v, t]) => (
          <button key={v} className={"kg-navlink" + (tab === v ? " on" : "")} onClick={() => { setTab(v); setEditing(null); setEditingShop(null); }}>{t}</button>
        ))}
      </div>

      {tab === "art" && (
        <div>
          {!editing && <button className="kg-btn kg-btn-gold" style={{ margin: "10px 0 6px" }} onClick={() => setEditing({ ...BLANK_ART })}><Ic d={P.plus} size={15} /> Add a painting</button>}
          {editing && <ArtEditor initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} notify={notify} />}
          <p className="kg-small kg-muted" style={{ margin: "14px 0 0" }}>
            The gallery shows paintings in this order, top to bottom. Use the arrows (or drag the ⠿ handle on a computer) to rearrange.
          </p>
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table className="kg-table">
              <thead><tr><th>Order</th><th>Painting</th><th>Price</th><th>Status</th><th>Featured</th><th></th></tr></thead>
              <tbody>
                {paintings.map((p, i) => (
                  <tr key={p.id} draggable onDragStart={() => setDragId(p.id)} onDragEnd={() => setDragId(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragId) moveTo(paintings.findIndex((x) => x.id === dragId), i); setDragId(null); }}
                    style={{ opacity: dragId === p.id ? 0.4 : 1 }}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span title="Drag to rearrange" style={{ cursor: "grab", color: "var(--ink2)", marginRight: 6 }}>⠿</span>
                      <button className="kg-iconbtn" aria-label={"Move " + p.title + " up"} disabled={i === 0} onClick={() => moveTo(i, i - 1)}>↑</button>
                      <button className="kg-iconbtn" aria-label={"Move " + p.title + " down"} disabled={i === paintings.length - 1} onClick={() => moveTo(i, i + 1)}>↓</button>
                    </td>
                    <td><div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <img src={resolveImg(p.images[0])} alt="" style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 4 }} />
                      <span style={{ fontStyle: "italic", fontFamily: "'Fraunces', Georgia, serif" }}>{p.title}</span></div></td>
                    <td>{cad.format(p.price)}</td>
                    <td><button className={"kg-pill " + (p.status === "sold" ? "sold" : "avail")} style={{ cursor: "pointer" }}
                      title="Toggle sold / available"
                      onClick={() => patchPainting(p.id, { status: p.status === "sold" ? "available" : "sold" })}>
                      {p.status === "sold" ? "● Sold" : "Available"}</button></td>
                    <td><input type="checkbox" aria-label={"Feature " + p.title} checked={!!p.featured}
                      onChange={() => patchPainting(p.id, { featured: !p.featured })} /></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="kg-iconbtn" aria-label={"Edit " + p.title} onClick={() => setEditing(p)}>✎</button>
                      <button className="kg-iconbtn" aria-label={"Remove " + p.title}
                        onClick={() => { if (window.confirm(`Remove “${p.title}” from the gallery?`)) removePainting(p.id); }}>
                        <Ic d={P.trash} size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "shop" && (
        <div>
          {!editingShop && (
            <button className="kg-btn kg-btn-gold" style={{ margin: "10px 0 6px" }}
              onClick={() => setEditingShop({
                ...BLANK_SHOP, kind: shopKind,
                variants: hasSizes(shopKind) ? BLANK_SHOP.variants.map((v) => ({ ...v })) : [onePriceVariant(0)],
              })}>
              <Ic d={P.plus} size={15} /> Add {kindLabel(shopKind).toLowerCase().replace(/s$/, "")}
            </button>
          )}
          {editingShop && <ShopEditor initial={editingShop} onSave={handleSaveShop} onCancel={() => setEditingShop(null)} notify={notify} />}

          <div className="kg-chips" style={{ margin: "16px 0 4px" }} role="group" aria-label="Which kind to list">
            {SHOP_KINDS.map((k) => {
              const n = (shopItems || []).filter((x) => x.kind === k.kind).length;
              return (
                <button key={k.kind} className={"kg-chip" + (shopKind === k.kind ? " on" : "")}
                  aria-pressed={shopKind === k.kind} onClick={() => setShopKind(k.kind)}>
                  {k.label} {n > 0 && <span className="kg-muted">({n})</span>}
                </button>
              );
            })}
          </div>

          <p className="kg-small kg-muted" style={{ margin: "10px 0 0" }}>
            These are reproductions, so they never sell out and never show as sold.
            Untick “In shop” to hide one without deleting it.
          </p>

          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table className="kg-table">
              <thead><tr><th>Item</th><th>{hasSizes(shopKind) ? "Sizes" : "Price"}</th>
                <th>{hasSizes(shopKind) ? "From" : ""}</th><th>In shop</th><th></th></tr></thead>
              <tbody>
                {(shopItems || []).filter((x) => x.kind === shopKind).map((x) => (
                  <tr key={x.id}>
                    <td><div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <img src={resolveImg((x.images || [])[0])} alt="" style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 4 }} />
                      <span style={{ fontStyle: "italic", fontFamily: "'Fraunces', Georgia, serif" }}>{x.title}</span></div></td>
                    <td className="kg-small">
                      {(x.variants || []).length === 0
                        ? <span className="kg-muted">none yet</span>
                        : hasSizes(x.kind)
                          ? (x.variants || []).map((v) => `${v.label} ${cad.format(v.price)}`).join(" · ")
                          : cad.format(fromPrice(x))}
                    </td>
                    <td>{hasSizes(x.kind) && fromPrice(x) > 0 ? cad.format(fromPrice(x)) : ""}</td>
                    <td><input type="checkbox" aria-label={"Show " + x.title + " in the shop"} checked={x.active !== false}
                      onChange={() => patchShopItem(x.id, { active: !(x.active !== false) })} /></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="kg-iconbtn" aria-label={"Edit " + x.title} onClick={() => setEditingShop(x)}>✎</button>
                      <button className="kg-iconbtn" aria-label={"Remove " + x.title}
                        onClick={() => { if (window.confirm(`Remove “${x.title}” from the shop?`)) removeShopItem(x.id); }}>
                        <Ic d={P.trash} size={15} /></button>
                    </td>
                  </tr>
                ))}
                {(shopItems || []).filter((x) => x.kind === shopKind).length === 0 && (
                  <tr><td colSpan={5} className="kg-muted kg-small" style={{ padding: "16px 10px" }}>
                    Nothing here yet. Use the button above to add your first one.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div style={{ overflowX: "auto" }}>
          {studioLoading ? <p style={{ color: "var(--ink2)" }}>Fetching your orders…</p> :
           orders.length === 0 ? <p style={{ color: "var(--ink2)" }}>No orders yet — when a visitor checks out or sends a purchase inquiry, it appears here.</p> : (
            <table className="kg-table">
              <thead><tr><th>Ref</th><th>Type</th><th>Buyer</th><th>Paintings</th><th>Total</th><th>Date</th><th>Status</th></tr></thead>
              <tbody>{orders.map((o) => (
                <tr key={o.ref}>
                  <td style={{ fontFamily: "monospace" }}>{o.ref}</td>
                  <td>{o.mode === "card" ? "Card order" : "Inquiry"}</td>
                  <td>{o.name}<br /><span style={{ color: "var(--ink2)", fontSize: 12 }}>{o.email}</span>{o.address ? <><br /><span style={{ color: "var(--ink2)", fontSize: 12 }}>{o.address}</span></> : null}{o.note ? <><br /><span style={{ fontSize: 12, fontStyle: "italic" }}>“{o.note}”</span></> : null}</td>
                  <td>{o.items.map((i) => i.title).join(", ")}</td>
                  <td>{cad.format(o.total)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(o.date).toLocaleDateString("en-CA")}</td>
                  <td><select className="kg-select" style={{ padding: "6px 8px", fontSize: 13 }} value={o.status}
                    aria-label={"Status for order " + o.ref}
                    onChange={(e) => patchOrder(o.ref, { status: e.target.value })}>
                    <option>New</option><option>Awaiting payment</option><option>Payment received</option>
                    <option>Payment abandoned</option><option>Check — already sold</option>
                    <option>Replied</option><option>Shipped</option><option>Complete</option><option>Cancelled</option>
                  </select></td>
                </tr>))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "msg" && (
        <div>
          {studioLoading ? <p style={{ color: "var(--ink2)" }}>Fetching your messages…</p> :
           messages.length === 0 ? <p style={{ color: "var(--ink2)" }}>No messages yet — notes from the contact page land here.</p> :
            messages.map((m, i) => (
              <div key={i} style={{ borderBottom: "1px solid var(--line)", padding: "14px 0" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                  <strong>{m.name}</strong><span style={{ color: "var(--ink2)", fontSize: 13 }}>{m.email}</span>
                  <span className="kg-pill">{m.topic}</span>
                  <span style={{ color: "var(--ink2)", fontSize: 12, marginLeft: "auto" }}>{new Date(m.date).toLocaleDateString("en-CA")}</span>
                </div>
                <p style={{ margin: "8px 0 0" }}>{m.message}</p>
              </div>
            ))}
        </div>
      )}

      {tab === "quotes" && (
        <div style={{ maxWidth: 560 }}>
          <p style={{ color: "var(--ink2)", fontSize: 14 }}>Add words from collectors — they'll appear on the home page automatically.</p>
          <div className="kg-field"><label htmlFor="t-name">Collector's name</label><input id="t-name" className="kg-input" value={tName} onChange={(e) => setTName(e.target.value)} /></div>
          <div className="kg-field"><label htmlFor="t-text">Their words</label><textarea id="t-text" className="kg-textarea" rows={2} value={tText} onChange={(e) => setTText(e.target.value)} /></div>
          <button className="kg-btn kg-btn-primary" disabled={!tName.trim() || !tText.trim()}
            onClick={async () => { const ok = await addTestimonial(tName.trim(), tText.trim()); if (ok) { setTName(""); setTText(""); } }}>Add testimonial</button>
          <div style={{ marginTop: 20 }}>
            {testimonials.map((t) => (
              <div key={t.id} style={{ display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid var(--line)", padding: "10px 0" }}>
                <div style={{ flex: 1 }}><em>“{t.text}”</em> <span className="kg-caps">— {t.name}</span></div>
                <button className="kg-iconbtn" aria-label={"Remove testimonial from " + t.name} onClick={() => removeTestimonial(t.id)}><Ic d={P.trash} size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "news" && (
        <div style={{ maxWidth: 480 }}>
          {studioLoading ? <p style={{ color: "var(--ink2)" }}>Fetching your list…</p> :
           news.length === 0 ? <p style={{ color: "var(--ink2)" }}>No subscribers yet — the signup form on the home page feeds this list.</p> :
            news.map((n, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--line)", padding: "10px 0", fontSize: 14 }}>
                <span>{n.email}</span><span style={{ color: "var(--ink2)", fontSize: 12 }}>{new Date(n.date).toLocaleDateString("en-CA")}</span>
              </div>
            ))}
        </div>
      )}

      {tab === "set" && (
        <div style={{ maxWidth: 560 }}>
          <div className="kg-field"><label>Home page main image</label>
            <p className="kg-small kg-muted" style={{ margin: "0 0 10px" }}>
              Choose any image for the big picture on the home page — it doesn't have to be in your gallery.
              With no image chosen, the first featured painting is shown.
            </p>
            {s.hero_image && (
              <img src={s.hero_image} alt="Current home page artwork"
                style={{ maxWidth: 220, maxHeight: 220, borderRadius: 6, border: "1px solid var(--line)", marginBottom: 12 }} />
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="kg-btn kg-btn-ghost" disabled={heroBusy} onClick={() => heroFileRef.current && heroFileRef.current.click()}>
                <Ic d={P.plus} size={15} /> {heroBusy ? "Uploading…" : s.hero_image ? "Replace image" : "Upload image"}</button>
              {s.hero_image && (
                <button className="kg-btn kg-btn-ghost" onClick={() => setS({ ...s, hero_image: "" })}>Use a gallery painting instead</button>
              )}
              <input ref={heroFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={pickHero} aria-label="Upload home page main image" />
            </div>
          </div>
          <div className="kg-field"><label>About page image</label>
            <p className="kg-small kg-muted" style={{ margin: "0 0 10px" }}>
              Choose any image for the About page — a photo of you, your studio, or a painting. With no image chosen,
              a featured painting is shown.
            </p>
            {s.about_image && (
              <img src={s.about_image} alt="Current About page artwork"
                style={{ maxWidth: 220, maxHeight: 220, borderRadius: 6, border: "1px solid var(--line)", marginBottom: 12 }} />
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="kg-btn kg-btn-ghost" disabled={aboutBusy} onClick={() => aboutFileRef.current && aboutFileRef.current.click()}>
                <Ic d={P.plus} size={15} /> {aboutBusy ? "Uploading…" : s.about_image ? "Replace image" : "Upload image"}</button>
              {s.about_image && (
                <button className="kg-btn kg-btn-ghost" onClick={() => setS({ ...s, about_image: "" })}>Use a gallery painting instead</button>
              )}
              <input ref={aboutFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={pickAbout} aria-label="Upload About page image" />
            </div>
          </div>
          <div className="kg-field"><label htmlFor="s-ig">Instagram URL</label><input id="s-ig" className="kg-input" value={s.instagram} onChange={(e) => setS({ ...s, instagram: e.target.value })} /></div>
          <div className="kg-field"><label htmlFor="s-yt">YouTube URL</label><input id="s-yt" className="kg-input" value={s.youtube} onChange={(e) => setS({ ...s, youtube: e.target.value })} /></div>
          <div className="kg-field"><label htmlFor="s-em">Contact email</label><input id="s-em" className="kg-input" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} /></div>
          <div className="kg-field"><label htmlFor="s-st">Artist statement (home page)</label><textarea id="s-st" className="kg-textarea" rows={4} value={s.statement} onChange={(e) => setS({ ...s, statement: e.target.value })} /></div>
          <button className="kg-btn kg-btn-primary" onClick={() => saveSettings(s)}>Save settings</button>
        </div>
      )}
    </div>
  );
}


export default Studio;
